import { Injectable, inject } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { map, Observable, catchError, of, switchMap } from 'rxjs';
import { StateService } from '../shared/state.service';
import { UtilityService } from '../shared/utility.service';
import { Encounter, Location, MedicationRequest, Task, ServiceRequest } from 'fhir/r4';

export interface ResourceWrapper<T> {
    referenceId: string | null;
    savedStatus: 'saved' | 'unsaved';
    actualResource: T;
}

@Injectable({
    providedIn: 'root'
})
export class NurseDashboardService {
    private stateService = inject(StateService);
    private utilityService = inject(UtilityService);

    /**
     * Vitals Pending: Pending ServiceRequests for vitals/observation
     */
    getVitalsPendingCount$(): Observable<number> {
        return this.utilityService.threeTierQueryAndSyncToState$<ServiceRequest>({
            resourceType: 'ServiceRequest',
            scope: 'orgWide',
            tier1FollowNextLinks: true
        }).pipe(
            map((requests: ServiceRequest[]) => {
                return (requests || []).filter(sr => {
                    const status = sr.status?.toLowerCase();
                    if (status !== 'active' && status !== 'draft' && status !== 'on-hold') {
                        return false;
                    }
                    const code = sr.code?.coding?.[0]?.code?.toLowerCase() || sr.code?.text?.toLowerCase() || '';
                    const display = sr.code?.coding?.[0]?.display?.toLowerCase() || '';
                    const category = sr.category?.[0]?.coding?.[0]?.code?.toLowerCase() || sr.category?.[0]?.text?.toLowerCase() || '';
                    
                    return code.includes('vital') || code.includes('observation') || 
                           display.includes('vital') || display.includes('observation') ||
                           category.includes('vital') || category.includes('observation');
                }).length;
            }),
            catchError(() => of(0))
        );
    }

    /**
     * Bed Occupancy: Locations of type 'bed' that are occupied
     */
    getBedOccupancyCount$(): Observable<number> {
        const locations$ = this.utilityService.threeTierQueryAndSyncToState$<Location>({
            resourceType: 'Location',
            scope: 'orgWide',
            tier1FollowNextLinks: true
        }).pipe(catchError(() => of([] as Location[])));

        const encounters$ = this.utilityService.threeTierQueryAndSyncToState$<Encounter>({
            resourceType: 'Encounter',
            scope: 'orgWide',
            tier1FollowNextLinks: true
        }).pipe(catchError(() => of([] as Encounter[])));

        return locations$.pipe(
            map((locations: Location[]) => {
                const beds = (locations || []).filter(loc => this.isBedLocation(loc));
                if (beds.length > 0) {
                    return beds.filter(loc => this.isBedOccupied(loc)).length;
                }
                return -1; // Indicates fallback needed
            }),
            catchError(() => of(-1))
        ).pipe(
            switchMap((count) => {
                if (count !== -1) return of(count);
                // Fallback: active inpatient encounters
                return encounters$.pipe(
                    map((encounters: Encounter[]) => {
                        return (encounters || []).filter(e => this.isActiveAdmissionEncounter(e)).length;
                    }),
                    catchError(() => of(0))
                );
            })
        );
    }

    private isBedLocation(location: any): boolean {
        const physicalType = location?.physicalType;
        if (physicalType) {
            const text = physicalType.text?.toLowerCase().trim();
            if (text === 'bed') return true;

            const codings = Array.isArray(physicalType.coding) ? physicalType.coding : [];
            if (codings.some((coding: any) => {
                const code = coding?.code?.toLowerCase().trim();
                const display = coding?.display?.toLowerCase().trim();
                return code === 'bd' || display === 'bed';
            })) {
                return true;
            }
        }

        const name = (location?.name ?? '').toString().toLowerCase();
        const description = (location?.description ?? '').toString().toLowerCase();
        return /\bbed\b/.test(name) || /\bbed\b/.test(description);
    }

    private isActiveAdmissionEncounter(encounter: any): boolean {
        return encounter?.status === 'in-progress' &&
            encounter?.class?.code === 'IMP' &&
            !!encounter?.hospitalization?.preAdmissionIdentifier?.value;
    }

    private isBedOccupied(location: any): boolean {
        const opStatus = location?.operationalStatus;
        if (!opStatus) return false;

        const codings = Array.isArray(opStatus.coding) ? opStatus.coding : [opStatus];
        return codings.some((coding: any) => {
            const code = (coding?.code ?? '').toString().toUpperCase();
            const display = (coding?.display ?? '').toString().toLowerCase();
            return code === 'O' || code === 'OCCUPIED' || display.includes('occupied');
        });
    }

    /**
     * Pending Medication Tasks
     */
    getPendingMedicationTasksCount$(): Observable<number> {
        return this.utilityService.threeTierQueryAndSyncToState$<Task>({
            resourceType: 'Task',
            scope: 'orgWide',
            tier1FollowNextLinks: true
        }).pipe(
            switchMap((tasks: Task[]) => {
                const pendingMedicationTasks = (tasks || []).filter((task) => {
                    const status = task.status?.toLowerCase();
                    return (status === 'requested' || status === 'in-progress' || status === 'ready')
                        && this.extractMedicationRequestIdFromTask(task) !== null;
                });

                if (!pendingMedicationTasks.length) {
                    return of(0);
                }

                return this.utilityService.threeTierQueryAndSyncToState$<MedicationRequest>({
                    resourceType: 'MedicationRequest',
                    scope: 'orgWide',
                    tier1FollowNextLinks: true
                }).pipe(
                    map((medicationRequests: MedicationRequest[]) => {
                        const activeMedicationRequestIds = new Set(
                            (medicationRequests || [])
                                .filter((request) => (request.status ?? '').toLowerCase() === 'active')
                                .map((request) => (request.id ?? '').toString().trim())
                                .filter(Boolean)
                        );

                        return pendingMedicationTasks.filter((task) => {
                            const medicationRequestId = this.extractMedicationRequestIdFromTask(task);
                            return medicationRequestId !== null && activeMedicationRequestIds.has(medicationRequestId);
                        }).length;
                    }),
                    catchError(() => of(0))
                );
            }),
            catchError(() => of(0))
        );
    }

    private extractMedicationRequestIdFromTask(task: Task): string | null {
        const reference = (task.focus?.reference ?? '').toString().trim();
        if (!reference.startsWith('MedicationRequest/')) {
            return null;
        }

        const id = reference.slice('MedicationRequest/'.length).split(/[/?#]/)[0].trim();
        return id || null;
    }
}
